import { defineBedrockEndpointContract } from "schemas/bedrockApi/contract";
import * as z from "zod";

const dateSchema = z.iso.date().describe("Inclusive UTC date, YYYY-MM-DD.");

export const heatmapDeviceSchema = z
  .enum(["desktop", "tablet", "mobile"])
  .describe("HeatmapDevice");

const heatmapDatesSchema = z.object({
  from: dateSchema,
  to: dateSchema,
});

const heatmapRangeSchema = heatmapDatesSchema.extend({
  device: heatmapDeviceSchema,
});

const variantKeySchema = z.string().min(1).max(64);
const pagePathSchema = z.string().min(1).max(2048);

export const heatmapPageRequestSchema = heatmapRangeSchema.extend({
  path: pagePathSchema,
  siteVersion: z
    .string()
    .max(64)
    .nullable()
    .default(null)
    .describe(
      "Only visits to this published version (from heatmaps.versions); an empty string means visits from sites that predate versions. Null for all versions in range.",
    ),
  experimentId: variantKeySchema
    .nullable()
    .describe(
      "Only visits to this A/B test (the split test's `replo-experiment-id`, `_rep_s` or replo:experiment). With a variantId, null means the variant was sent without an experiment; otherwise null means any.",
    ),
  variantId: variantKeySchema
    .nullable()
    .describe(
      "Only visits to this variant (`replo-variation`, `_rep_v` or replo:variant); null for all visits.",
    ),
});

/** Positions are fractions of page width (`x`) and page height (`y`). */
export const heatmapClickPointSchema = z
  .object({
    selector: z.string(),
    rx: z.number(),
    ry: z.number(),
    x: z.number(),
    y: z.number(),
    weight: z.number(),
  })
  .describe("HeatmapClickPoint");

export const heatmapScrollBucketSchema = z
  .object({
    pct: z.number().describe("Deepest point reached, % of page height."),
    views: z.number(),
  })
  .describe("HeatmapScrollBucket");

export const heatmapElementSchema = z
  .object({
    selector: z.string(),
    text: z.string(),
    clicks: z.number(),
    sessions: z.number(),
    rageClicks: z.number(),
    position: z.number().describe("Average click depth, 0–1 of page height."),
    cartSessions: z
      .number()
      .describe("Sessions that added to cart after clicking it."),
    purchaseSessions: z
      .number()
      .describe("Sessions that purchased after clicking it."),
    purchases: z
      .number()
      .describe("Orders those sessions placed after the click."),
    revenue: z
      .number()
      .describe("USD of purchases in those sessions made after the click."),
  })
  .describe("HeatmapElement");

export const heatmapVariantSummarySchema = z
  .object({
    experimentId: z
      .string()
      .nullable()
      .describe("Null for a variant sent without an experiment."),
    variantId: z.string(),
    sessions: z.number(),
    clicks: z.number(),
    purchaseSessions: z.number(),
    revenue: z.number().describe("USD."),
  })
  .describe("HeatmapVariantSummary");

export const heatmapTotalsSchema = z
  .object({
    sessions: z.number(),
    clicks: z.number(),
    rageClicks: z.number(),
    cartSessions: z
      .number()
      .describe("Visits that added to cart after landing on the page."),
    purchaseSessions: z
      .number()
      .describe(
        "Visits that bought after landing on the page; each visit counts once.",
      ),
    purchases: z.number().describe("Orders those visits placed."),
    revenue: z
      .number()
      .describe("USD, each purchase converted at its date's rate."),
  })
  .describe("HeatmapTotals");

export const heatmapPageResponseSchema = z
  .object({
    path: z.string(),
    origin: z.string().nullable(),
    totals: heatmapTotalsSchema,
    page: z.object({
      width: z.number().describe("Median viewport width of the visits."),
      height: z.number().describe("Median page height at that width."),
      fold: z.number().describe("Median viewport height."),
    }),
    clicks: z.array(heatmapClickPointSchema),
    scroll: z.array(heatmapScrollBucketSchema),
    elements: z.array(heatmapElementSchema),
    variants: z.array(heatmapVariantSummarySchema),
  })
  .describe("HeatmapPageResponse");

export const heatmapPageCaptureSchema = z
  .enum(["heatmap", "clicks"])
  .describe(
    "HeatmapPageCapture: what was recorded on the page, whatever hosts it. `heatmap`: Replo's page script ran (any website), so clicks have positions and the page has snapshots. `clicks`: only Replo's Shopify web pixel ran, which reports which element was clicked but not where.",
  );

export const heatmapPagesRequestSchema = heatmapDatesSchema.extend({
  search: z
    .string()
    .max(200)
    .optional()
    .describe("Only pages whose path contains this text, ignoring case."),
  device: heatmapDeviceSchema
    .optional()
    .describe(
      "Count `heatmap` pages' visits from this device only, as heatmaps.page does; every device when absent. `clicks` pages have no device and always count every visit.",
    ),
});

export const heatmapPageSummarySchema = z
  .object({
    path: z.string(),
    origin: z.string().nullable(),
    capture: heatmapPageCaptureSchema,
    sessions: z
      .number()
      .describe(
        "Visits counted as the page's own totals count them: heatmaps.page (all versions) for `heatmap` pages, heatmaps.clickedElements for `clicks` pages.",
      ),
    clicks: z.number(),
    purchaseSessions: z.number(),
    revenue: z.number().describe("USD."),
    latestSiteVersion: z
      .string()
      .nullable()
      .describe(
        "Newest version with visits (a publish id, or the page script's own `auto-` id); null for `clicks` pages.",
      ),
    hasSiteSnapshot: z
      .boolean()
      .default(false)
      .describe(
        "The newest version has a site snapshot, so the page can be drawn; always false for `clicks` pages.",
      ),
  })
  .describe("HeatmapPageSummary");

export const heatmapPagesResponseSchema = z
  .object({
    pages: z.array(heatmapPageSummarySchema),
  })
  .describe("HeatmapPages");

export const heatmapClickedElementsRequestSchema = heatmapDatesSchema.extend({
  path: pagePathSchema,
});

export const heatmapClickedElementSchema = z
  .object({
    key: z
      .string()
      .describe(
        "Link path, else `tag#id`, else the tag name; stable per element.",
      ),
    label: z.string(),
    tag: z.string(),
    clicks: z.number(),
    sessions: z.number(),
    cartSessions: z.number(),
    purchaseSessions: z.number(),
    purchases: z.number(),
    revenue: z.number().describe("USD."),
  })
  .describe("HeatmapClickedElement");

export const heatmapClickedElementsResponseSchema = z
  .object({
    path: z.string(),
    totals: heatmapTotalsSchema,
    elements: z.array(heatmapClickedElementSchema),
  })
  .describe("HeatmapClickedElements");

export const heatmapVersionsRequestSchema = heatmapRangeSchema.extend({
  path: pagePathSchema,
});

export const heatmapVersionSchema = z
  .object({
    siteVersion: z
      .string()
      .describe(
        "The publish the visits were served from; empty for visits from sites that predate versions.",
      ),
    firstSeen: z.iso.datetime().describe("First visit in range."),
    lastSeen: z.iso.datetime().describe("Last visit in range."),
    sessions: z.number(),
    purchaseSessions: z
      .number()
      .describe(
        "Visits to this version that bought after landing on the page; each visit counts once.",
      ),
    revenue: z
      .number()
      .describe(
        "USD of those purchases, each converted at its date's rate. Matches heatmaps.page totals for this version.",
      ),
    hasSiteSnapshot: z
      .boolean()
      .describe(
        "A snapshot of the page as visitors saw it was captured; read it with heatmaps.siteSnapshot.",
      ),
  })
  .describe("HeatmapVersion");

export const heatmapVersionsResponseSchema = z
  .object({
    versions: z.array(heatmapVersionSchema),
  })
  .describe("HeatmapVersions");

const SITE_SNAPSHOT_KEY_PREFIX = "site-snapshots";

/** Publish ids are UUIDs; anything else never names a stored site snapshot. */
export const SITE_SNAPSHOT_VERSION_PATTERN = /^[\w-]{1,64}$/;

/** Same rule heatmap events use for their path (no query, no trailing slash), so snapshots and heat line up. */
export function normalizeSiteSnapshotPath(path: string): string {
  return path.replace(/[?#].*$/, "").replace(/\/$/, "") || "/";
}

/** R2 prefix holding every site snapshot of one project. */
export function getSiteSnapshotProjectPrefix(projectId: string): string {
  return `${SITE_SNAPSHOT_KEY_PREFIX}/${projectId}/`;
}

/** R2 key of a site snapshot, shared by analytics-fire, which writes it, and bedrock, which reads it. */
export function getSiteSnapshotKey({
  projectId,
  siteVersion,
  path,
}: {
  projectId: string;
  siteVersion: string;
  path: string;
}): string {
  return `${getSiteSnapshotProjectPrefix(projectId)}${siteVersion}/${toBase64Url(path)}.json.gz`;
}

/** Unpadded base64url of the UTF-8 bytes, so any path is one safe key segment. */
function toBase64Url(value: string): string {
  let binary = "";
  for (const byte of new TextEncoder().encode(value)) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

export const heatmapSiteSnapshotRequestSchema = z.object({
  siteVersion: z
    .string()
    .regex(SITE_SNAPSHOT_VERSION_PATTERN)
    .describe("A version from heatmaps.versions with `hasSiteSnapshot`."),
  path: pagePathSchema,
});

const projectParamsSchema = z.object({ projectId: z.uuid() });

const heatmapPageEndpoint = defineBedrockEndpointContract({
  operationId: "heatmaps.page",
  method: "post",
  path: "/api/v1/projects/{projectId}/heatmaps/page",
  routePath: "/{projectId}/heatmaps/page",
  description:
    "Clicks, scroll reach, elements and variant results for one page",
  request: { params: projectParamsSchema, body: heatmapPageRequestSchema },
  response: {
    type: "json",
    schema: z.object({ data: heatmapPageResponseSchema }),
  },
});

const heatmapVersionsEndpoint = defineBedrockEndpointContract({
  operationId: "heatmaps.versions",
  method: "post",
  path: "/api/v1/projects/{projectId}/heatmaps/versions",
  routePath: "/{projectId}/heatmaps/versions",
  description:
    "Published versions of one page that had visits in range, newest first, each with its outcomes and whether its site snapshot exists",
  request: { params: projectParamsSchema, body: heatmapVersionsRequestSchema },
  response: {
    type: "json",
    schema: z.object({ data: heatmapVersionsResponseSchema }),
  },
});

const heatmapSiteSnapshotEndpoint = defineBedrockEndpointContract({
  operationId: "heatmaps.siteSnapshot",
  method: "post",
  path: "/api/v1/projects/{projectId}/heatmaps/site-snapshot",
  routePath: "/{projectId}/heatmaps/site-snapshot",
  description:
    "The gzipped rrweb snapshot of one page as visitors saw it in one published version (`application/gzip`)",
  request: {
    params: projectParamsSchema,
    body: heatmapSiteSnapshotRequestSchema,
  },
  response: { type: "binary" },
});

const heatmapPagesEndpoint = defineBedrockEndpointContract({
  operationId: "heatmaps.pages",
  method: "post",
  path: "/api/v1/projects/{projectId}/heatmaps/pages",
  routePath: "/{projectId}/heatmaps/pages",
  description:
    "Pages of the project with clicks or scroll data in range, busiest first, from any website running Replo's page script or Shopify pixel",
  request: { params: projectParamsSchema, body: heatmapPagesRequestSchema },
  response: {
    type: "json",
    schema: z.object({ data: heatmapPagesResponseSchema }),
  },
});

const heatmapClickedElementsEndpoint = defineBedrockEndpointContract({
  operationId: "heatmaps.clickedElements",
  method: "post",
  path: "/api/v1/projects/{projectId}/heatmaps/clicked-elements",
  routePath: "/{projectId}/heatmaps/clicked-elements",
  description:
    "Clicked elements of one page that only has Shopify-pixel clicks, with what visitors bought after clicking them",
  request: {
    params: projectParamsSchema,
    body: heatmapClickedElementsRequestSchema,
  },
  response: {
    type: "json",
    schema: z.object({ data: heatmapClickedElementsResponseSchema }),
  },
});

export const heatmapsEndpointContracts = {
  page: heatmapPageEndpoint,
  pages: heatmapPagesEndpoint,
  clickedElements: heatmapClickedElementsEndpoint,
  versions: heatmapVersionsEndpoint,
  siteSnapshot: heatmapSiteSnapshotEndpoint,
} as const;
